import { useState, useCallback } from 'react';
import useSWR, { preload } from 'swr';
import {
  listOrgCustomRolesApiv2,
  createOrgCustomRoleApiv2,
  updateOrgCustomRoleApiv2,
  deleteOrgCustomRoleApi,
} from 'src/api/org-roles';
import type { OrgCustomRole, OrgPermission } from '@equaliq/api-model';

const fetcher = async (orgId: string) => {
  const response = await listOrgCustomRolesApiv2({ orgId });
  return Object.values(response.roles ?? {}) as OrgCustomRole[];
};

export const preloadOrgRoles = (orgId: string) =>
  preload(['roles', orgId], () => fetcher(orgId)).catch(() => undefined);

export const useOrgRoles = (orgId?: string) => {
  const swrKey = orgId ? ['roles', orgId] : null;

  const { data, error, isLoading, mutate } = useSWR(swrKey, () => fetcher(orgId!));

  const customRoles = data ?? [];

  // UI state — local useState per 004_state.txt
  const [editingRole, setEditingRole] = useState<OrgCustomRole | null>(null);

  const refreshCustomRoles = async () => {
    await mutate();
  };

  const createCustomRole = useCallback(async (
    name: string,
    description?: string,
    permissions?: OrgPermission[]
  ) => {
    await createOrgCustomRoleApiv2({
      orgId: orgId!,
      name,
      description,
      permissions: permissions || [],
    });
    await mutate();
  }, [orgId, mutate]);

  const updateCustomRole = useCallback(async (
    customRoleId: string,
    name?: string,
    description?: string,
    permissions?: OrgPermission[]
  ) => {
    await mutate(
      customRoles.map(r =>
        r.customRoleId === customRoleId
          ? {
              ...r,
              name: name || r.name,
              description: description !== undefined ? description : r.description,
              permissions: permissions || r.permissions,
            }
          : r
      ),
      false
    );

    try {
      await updateOrgCustomRoleApiv2({ orgId: orgId!, customRoleId, name, description, permissions });
      await mutate();
    } catch (err) {
      await mutate();
      throw err;
    }
    setEditingRole(null);
  }, [orgId, customRoles, mutate]);

  const deleteCustomRole = useCallback(async (customRoleId: string) => {
    await mutate(
      customRoles.filter(r => r.customRoleId !== customRoleId),
      false
    );

    try {
      await deleteOrgCustomRoleApi({ orgId: orgId!, customRoleId });
      await mutate();
    } catch (err) {
      await mutate();
      throw err;
    }
  }, [orgId, customRoles, mutate]);

  const startEditingRole = useCallback((role: OrgCustomRole) => {
    setEditingRole(role);
  }, []);

  const cancelEditingRole = useCallback(() => {
    setEditingRole(null);
  }, []);

  return {
    customRoles,
    isLoading,
    error,
    editingRole,
    refreshCustomRoles,
    createCustomRole,
    updateCustomRole,
    deleteCustomRole,
    startEditingRole,
    cancelEditingRole,
  };
};
